import React from 'react';
import { getEnvironmentDisplayName, getEnvironmentColors } from '../../utils/getEnvironment';

/**
 * Environment Badge Component - Toggle Switch Style (Read-only)
 * Displays the current environment (DEV, STAGING, or LIVE) as a toggle switch indicator
 */
export default function EnvironmentBadge({ 
  className = '', 
  variant = 'header' // 'header', 'corner', 'banner'
}) {
  const envName = getEnvironmentDisplayName();
  const colors = getEnvironmentColors();
  
  // Toggle switch style component
  const ToggleSwitch = ({ size = 'normal' }) => {
    const isLarge = size === 'large';
    const toggleWidth = isLarge ? '52px' : '44px';
    const toggleHeight = isLarge ? '28px' : '24px';
    const circleSize = isLarge ? '22px' : '18px';
    const textSize = isLarge ? 'text-base' : 'text-sm';
    const gap = isLarge ? 'gap-3' : 'gap-2';
    
    return (
      <div className={`flex items-center ${gap}`}>
        {/* Toggle Switch Container */}
        <div
          className="relative inline-flex items-center cursor-default"
          style={{
            width: toggleWidth,
            height: toggleHeight,
            borderRadius: '14px',
            backgroundColor: colors.bg,
            transition: 'all 0.3s ease',
            boxShadow: `inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.15)`,
          }}
        >
          {/* Toggle Switch Circle (always on the right - "on" position) */}
          <div
            className="absolute right-1 top-1/2 transform -translate-y-1/2"
            style={{
              width: circleSize,
              height: circleSize,
              borderRadius: '50%',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              transition: 'all 0.3s ease',
            }}
          />
        </div>
        
        {/* Environment Label */}
        <span
          className={`${textSize} font-semibold`}
          style={{
            color: colors.bg,
            letterSpacing: '0.05em',
            textShadow: isLarge ? '0 1px 2px rgba(255, 255, 255, 0.3)' : 'none',
          }}
        >
          {envName}
        </span>
      </div>
    );
  };
  
  if (variant === 'header') {
    // Header style - appears in the header area
    return (
      <div className={`flex items-center ${className}`}>
        <ToggleSwitch size="normal" />
      </div>
    );
  }
  
  if (variant === 'corner') {
    // Corner style - fixed top-right with backdrop for visibility
    return (
      <div
        className={`fixed top-0 right-0 z-50 ${className}`}
        style={{
          pointerEvents: 'none',
          padding: '16px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: '0 0 0 12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <ToggleSwitch size="large" />
      </div>
    );
  }
  
  if (variant === 'banner') {
    // Banner style - full width at top
    return (
      <div
        className={`w-full py-3 px-6 flex items-center justify-end bg-white border-b ${className}`}
        style={{
          borderColor: '#E5E7EB',
        }}
      >
        <ToggleSwitch size="normal" />
      </div>
    );
  }
  
  // Default inline
  return (
    <div className={`inline-flex items-center ${className}`}>
      <ToggleSwitch size="normal" />
    </div>
  );
}

